import { useState } from 'react';
import { ChevronRight, ChevronDown, Calendar, AlertTriangle, Plus, Pencil, Trash2, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Submission, SubmissionType } from '@/hooks/useSubmissions';
import type { Deliverable, DeliverableStatus, DeliverableUpdate } from '@/hooks/useDeliverables';
import type { TeamMember } from '@/hooks/useTeamMembers';
import type { SiteVisit } from '@/hooks/useSiteVisits';

const SUBMISSION_TYPE_LABELS: Record<SubmissionType, string> = {
  permit: 'Permit',
  client_submission: 'Client',
  internal: 'Internal',
  engineering: 'Engineering',
  general: 'General',
};

const SUBMISSION_TYPE_COLORS: Record<SubmissionType, string> = {
  permit: 'bg-blue-50 text-blue-700 border-blue-200',
  client_submission: 'bg-purple-50 text-purple-700 border-purple-200',
  internal: 'bg-gray-50 text-gray-700 border-gray-200',
  engineering: 'bg-teal-50 text-teal-700 border-teal-200',
  general: 'bg-slate-50 text-slate-700 border-slate-200',
};

function getProgressColor(completed: number, total: number, hasOverdue: boolean, activeCount: number): string {
  if (hasOverdue) return 'border-l-red-500 bg-red-50/30';
  if (total === 0) return 'bg-gray-50/50';
  const pct = completed / total;
  if (pct >= 1) return 'border-l-green-500 bg-green-50/30';
  if (pct >= 0.5) return 'border-l-blue-500 bg-blue-50/20';
  if (activeCount > 0) return 'border-l-blue-400 bg-blue-50/10';
  return 'bg-gray-50/30';
}

interface SubmissionGroupProps {
  submission: Submission;
  deliverables: Deliverable[];
  siteVisits?: SiteVisit[];
  isExpanded: boolean;
  onToggle: () => void;
  onUpdate: (id: string, data: { name?: string; type?: SubmissionType; due_date?: string }) => void;
  onDelete: (id: string) => void;
  renderDeliverables: (deliverables: Deliverable[]) => React.ReactNode;
  onAddDeliverable: (submissionId: string) => void;
}

export function SubmissionGroup({
  submission,
  deliverables,
  siteVisits = [],
  isExpanded,
  onToggle,
  onUpdate,
  onDelete,
  renderDeliverables,
  onAddDeliverable,
}: SubmissionGroupProps) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');

  const completed = submission.completed_count;
  const total = submission.deliverable_count;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  // Use backend active_count (always accurate), fall back to client-side count
  const activeStatuses: DeliverableStatus[] = ['in_progress', 'in_review', 'revision'];
  const activeCount = submission.active_count ?? deliverables.filter(d => activeStatuses.includes(d.status)).length;

  const hasOverdue = deliverables.some(d => {
    if (!d.due_date || d.status === 'delivered' || d.status === 'approved') return false;
    return new Date(d.due_date + 'T00:00:00') < new Date();
  });

  const isSubmissionOverdue = submission.due_date && new Date(submission.due_date + 'T00:00:00') < new Date() && pct < 100;

  const bgColor = getProgressColor(completed, total, hasOverdue || !!isSubmissionOverdue, activeCount);

  return (
    <div className={`rounded-lg border border-l-4 overflow-hidden ${bgColor}`}>
      {/* Header */}
      <div
        className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-accent/30 transition-colors group"
        onClick={onToggle}
      >
        {/* Expand chevron */}
        {isExpanded
          ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        }

        {/* Name (click-to-edit) */}
        {isEditingName ? (
          <Input
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={() => {
              if (nameValue.trim() && nameValue !== submission.name) {
                onUpdate(submission.id, { name: nameValue.trim() });
              }
              setIsEditingName(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') setIsEditingName(false);
            }}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            className="h-7 text-sm font-semibold w-[250px]"
          />
        ) : (
          <span
            className="text-sm font-semibold hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              setNameValue(submission.name);
              setIsEditingName(true);
            }}
          >
            {submission.name}
          </span>
        )}

        {/* Type badge */}
        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border whitespace-nowrap ${SUBMISSION_TYPE_COLORS[submission.type as SubmissionType] || SUBMISSION_TYPE_COLORS.general}`}>
          {SUBMISSION_TYPE_LABELS[submission.type as SubmissionType] || submission.type}
        </span>

        {/* Progress bar — multi-segment: green (delivered) + blue (active) */}
        <div className="flex items-center gap-2 ml-auto">
          <div className="w-24 h-2 rounded-full bg-gray-200 overflow-hidden flex">
            {completed > 0 && (
              <div
                className="h-full bg-green-500 transition-all"
                style={{ width: `${(completed / total) * 100}%` }}
              />
            )}
            {activeCount > 0 && (
              <div
                className="h-full bg-blue-400 transition-all"
                style={{ width: `${(activeCount / total) * 100}%` }}
              />
            )}
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {completed}/{total}{activeCount > 0 && pct < 100 && (
              <span className="text-blue-600"> ({activeCount} active)</span>
            )}
          </span>
        </div>

        {/* Due date */}
        {submission.due_date && (
          <span className={`flex items-center gap-1 text-xs whitespace-nowrap ${
            isSubmissionOverdue ? 'text-red-600 font-medium' : 'text-muted-foreground'
          }`}>
            {isSubmissionOverdue && <AlertTriangle className="h-3 w-3" />}
            <Calendar className="h-3 w-3" />
            {new Date(submission.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}

        {/* Delete button */}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          onClick={(e) => { e.stopPropagation(); onDelete(submission.id); }}
          title="Delete submission"
        >
          <Trash2 className="h-3 w-3 text-destructive" />
        </Button>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t">
          <div className="px-3 py-1">
            {deliverables.length > 0 ? (
              renderDeliverables(deliverables)
            ) : (
              <div className="text-center py-4 text-xs text-muted-foreground">
                No deliverables in this submission yet.
              </div>
            )}
          </div>
          {siteVisits.length > 0 && (
            <div className="px-3 pb-2 border-t pt-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <Camera className="h-3 w-3" />
                Site visits ({siteVisits.length})
              </p>
              <ul className="space-y-1">
                {siteVisits.map((v) => (
                  <li key={v.id} className="flex items-center gap-2 text-xs">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] border ${
                      v.status === 'submitted' ? 'bg-green-50 text-green-700 border-green-200' :
                      v.status === 'completed' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      'bg-gray-50 text-gray-600 border-gray-200'
                    }`}>
                      {v.status}
                    </span>
                    <span className="font-medium text-foreground truncate">{v.title}</span>
                    <span className="text-muted-foreground whitespace-nowrap ml-auto">
                      {new Date(v.visit_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="px-3 pb-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground text-xs"
              onClick={() => onAddDeliverable(submission.id)}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add deliverable
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
